/**
 * Redesigned navigation behaviour.
 * - Desktop: hover / focus / first-tap opens a full-width panel per top-level item.
 * - Search links ([data-nav2-search]) close the menu and open the Algolia search box,
 *   prefilled with the item's term. Falls back to /search?q= if Algolia is not ready.
 * - Campaign tiles hide after their end time and run an optional countdown.
 */
(function () {
  "use strict";

  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 200;
  let campaignScheduleTimer;

  function initDesktop(nav) {
    const items = Array.from(nav.querySelectorAll(".nav2__item--has-panel"));
    let openItem = null;
    let openTimer = null;
    let closeTimer = null;

    function open(item) {
      if (openItem === item) return;
      if (openItem) close(false);
      openItem = item;
      item.classList.add("is-open");
      // Keep tall panels within the screen on small laptops; they scroll inside instead
      const panel = item.querySelector(".nav2__panel");
      panel.style.maxHeight = `${Math.max(240, window.innerHeight - panel.getBoundingClientRect().top - 8)}px`;
      item.querySelector(".nav2__toggle").setAttribute("aria-expanded", "true");
    }

    function close(clearTimers = true) {
      if (clearTimers) {
        clearTimeout(openTimer);
        clearTimeout(closeTimer);
      }
      if (!openItem) return;
      openItem.classList.remove("is-open");
      openItem.querySelector(".nav2__toggle").setAttribute("aria-expanded", "false");
      openItem = null;
    }

    items.forEach((item) => {
      const link = item.querySelector(".nav2__link");

      item.addEventListener("mouseenter", () => {
        clearTimeout(closeTimer);
        clearTimeout(openTimer);
        // Switch instantly when another panel is already open
        openTimer = setTimeout(() => open(item), openItem ? 0 : OPEN_DELAY);
      });

      item.addEventListener("mouseleave", () => {
        clearTimeout(openTimer);
        closeTimer = setTimeout(() => close(), CLOSE_DELAY);
      });

      link.addEventListener("click", (event) => {
        // Touch screens at desktop width: first tap opens the panel, second tap follows the link
        if (window.matchMedia("(hover: none)").matches && openItem !== item) {
          event.preventDefault();
          open(item);
        }
      });

      // Keyboard: category links tab straight across the bar; the toggle after each one opens its panel
      item.querySelector(".nav2__toggle").addEventListener("click", () => {
        if (openItem === item) {
          close();
        } else {
          clearTimeout(openTimer);
          open(item);
        }
      });
    });

    // Moving focus to another category closes the open panel
    nav.addEventListener("focusin", (event) => {
      if (openItem && !openItem.contains(event.target)) close();
    });

    nav.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !openItem) return;
      const toggle = openItem.querySelector(".nav2__toggle");
      close();
      toggle.focus();
    });

    document.addEventListener("click", (event) => {
      if (openItem && !nav.contains(event.target)) close();
    });

    nav.addEventListener("focusout", () => {
      setTimeout(() => {
        if (openItem && !nav.contains(document.activeElement)) close();
      });
    });

    return close;
  }

  function setInputValue(input, value) {
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value").set;
    setter.call(input, value);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function closeDrawer() {
    const drawer = document.querySelector("header-drawer");
    const details = drawer && drawer.querySelector("#Details-menu-drawer-container");
    if (!details || !details.hasAttribute("open")) return;
    drawer.closeMenuDrawer(new Event("click"), details.querySelector("summary"));
  }

  function openSearch(term) {
    const input = document.querySelector("#pd-main-searchbox .aa-Input, .header .aa-Input, #pd-main-searchbox-input");
    if (input) {
      input.focus();
      if (term) setInputValue(input, term);
      return true;
    }

    const detachedButton = document.querySelector(".aa-DetachedSearchButton");
    if (detachedButton) {
      detachedButton.click();
      setTimeout(() => {
        const overlayInput = document.querySelector(".aa-DetachedOverlay .aa-Input");
        if (!overlayInput) return;
        overlayInput.focus();
        if (term) setInputValue(overlayInput, term);
      }, 50);
      return true;
    }

    return false;
  }

  function initSearchLinks(closeDesktop) {
    document.addEventListener("click", (event) => {
      const link = event.target.closest("[data-nav2-search]");
      if (!link) return;

      const term = link.dataset.nav2Search || "";
      closeDrawer();
      if (closeDesktop) closeDesktop();
      if (openSearch(term)) event.preventDefault();
    });
  }

  function updateCampaignSchedules() {
    clearTimeout(campaignScheduleTimer);
    const now = Date.now();
    let nextChange = Infinity;

    document.querySelectorAll("[data-nav2-campaign], [data-nav2-promo]").forEach((tile) => {
      const starts = Date.parse(tile.dataset.nav2Starts || "");
      const ends = Date.parse(tile.dataset.nav2Ends || "");
      if (!isNaN(ends) && ends <= now) {
        tile.remove();
        return;
      }
      if (tile.hasAttribute("data-nav2-promo")) {
        tile.hidden = !isNaN(starts) && starts > now;
      }
      if (starts > now) nextChange = Math.min(nextChange, starts);
      if (ends > now) nextChange = Math.min(nextChange, ends);
    });

    if (Number.isFinite(nextChange)) {
      campaignScheduleTimer = setTimeout(updateCampaignSchedules, Math.min(nextChange - now, 2147483647));
    }
  }

  function initCampaigns() {
    updateCampaignSchedules();

    // One-line countdown: "Starts in 25 days", then hours on the last day, then minutes, then the done text
    const lines = Array.from(document.querySelectorAll("[data-nav2-countdown-line]"));
    function tickLines() {
      lines.forEach((el) => {
        const target = Date.parse(el.dataset.nav2CountdownLine);
        if (isNaN(target)) return;
        const mins = Math.floor((target - Date.now()) / 60000);
        let text;
        if (mins <= 0) text = el.dataset.done || "";
        else if (mins >= 2880) text = `${el.dataset.prefix} ${Math.floor(mins / 1440)} days`;
        else if (mins >= 120) text = `${el.dataset.prefix} ${Math.floor(mins / 60)} hours`;
        else if (mins >= 60) text = `${el.dataset.prefix} 1 hour`;
        else text = `${el.dataset.prefix} ${mins} ${mins === 1 ? "minute" : "minutes"}`;
        el.textContent = text;
        el.hidden = !text;
      });
    }
    if (lines.length) {
      tickLines();
      setInterval(tickLines, 30000);
    }

    // Plain tile countdown: value updates (25 days, then hours, then minutes); swaps to the done text at zero
    const plains = Array.from(document.querySelectorAll("[data-nav2-countdown-plain]"));
    function tickPlains() {
      plains.forEach((el) => {
        const target = Date.parse(el.dataset.nav2CountdownPlain);
        if (isNaN(target)) return;
        const mins = Math.floor((target - Date.now()) / 60000);
        const done = el.parentElement.querySelector("[data-nav2-countdown-done]");
        if (mins <= 0) {
          el.hidden = true;
          if (done) done.hidden = false;
          return;
        }
        let value;
        if (mins >= 2880) value = `${Math.floor(mins / 1440)} days`;
        else if (mins >= 1440) value = "1 day";
        else if (mins >= 120) value = `${Math.floor(mins / 60)} hours`;
        else if (mins >= 60) value = "1 hour";
        else value = `${mins} ${mins === 1 ? "min" : "mins"}`;
        el.querySelector("[data-value]").textContent = value;
        el.hidden = false;
        if (done) done.hidden = true;
      });
    }
    if (plains.length) {
      tickPlains();
      setInterval(tickPlains, 30000);
    }

    const countdowns = Array.from(document.querySelectorAll("[data-nav2-countdown]"));
    if (!countdowns.length) return;

    function tick() {
      countdowns.forEach((el) => {
        const target = Date.parse(el.dataset.nav2Countdown);
        const diff = target - Date.now();
        if (isNaN(target) || diff <= 0) {
          el.hidden = true;
          return;
        }
        const mins = Math.floor(diff / 60000);
        el.querySelector('[data-unit="d"]').textContent = Math.floor(mins / 1440);
        el.querySelector('[data-unit="h"]').textContent = Math.floor((mins % 1440) / 60);
        el.querySelector('[data-unit="m"]').textContent = mins % 60;
        el.hidden = false;
      });
    }

    tick();
    setInterval(tick, 30000);
  }

  // Mobile drawer: "All categories" reveals the remaining cards in the same grid; the toggle stays after the last card
  function setMore(toggle, open) {
    const grid = document.getElementById(toggle.getAttribute("aria-controls"));
    if (!grid) return;
    toggle.setAttribute("aria-expanded", String(open));
    toggle.dataset.clarityMenuAction = open ? "close" : "open";
    toggle.querySelector("[data-nav2-more-label]").textContent = open ? "Fewer categories" : "All categories";
    const extra = grid.querySelectorAll(".nav2-drawer__cat--extra").length;
    toggle.querySelector("[data-nav2-more-count]").textContent = open ? "" : `(+${extra})`;
    grid.classList.toggle("is-expanded", open);
  }

  // Keyboard: Space activates role="button" links and spans (the categories toggle and the menu close icons)
  function initDrawerKeys() {
    document.addEventListener("keydown", (event) => {
      if (event.key !== " " && event.key !== "Enter") return;
      const control = event.target.closest("#menu-drawer [role='button'][data-nav2-more], #menu-drawer .menu-drawer__submenu-close[role='button']");
      if (!control) return;
      // Enter already follows the categories toggle link; only add Enter for the close spans
      if (event.key === "Enter" && control.hasAttribute("data-nav2-more")) return;
      event.preventDefault();
      control.click();
    });
  }

  function initDrawerMore() {
    // Expanded by default; this just fills in the count used once compacted
    const first = document.querySelector("[data-nav2-more]");
    if (first) setMore(first, true);

    document.addEventListener("click", (event) => {
      const toggle = event.target.closest("[data-nav2-more]");
      if (!toggle) return;
      event.preventDefault();
      const open = toggle.getAttribute("aria-expanded") !== "true";
      setMore(toggle, open);
      // Collapsing from the bottom moves the toggle up; keep it under the thumb
      if (!open) toggle.scrollIntoView({ block: "nearest" });
    });
  }

  // Mobile category strip "All" opens the menu with every category already expanded
  function initStrip() {
    document.addEventListener("click", (event) => {
      const link = event.target.closest("[data-nav2-open-menu]");
      if (!link) return;
      const drawerToggle = document.querySelector("#Details-menu-drawer-container > summary");
      const more = document.querySelector("[data-nav2-more]");
      if (!drawerToggle || !more) return;
      event.preventDefault();
      setMore(more, true);
      drawerToggle.click();
      // Land with the "Shop by category" heading just under the pinned search row, so the first cards aren't hidden behind it
      setTimeout(() => {
        const scroller = document.querySelector("#menu-drawer .menu-drawer__navigation-container");
        const section = document.getElementById(more.getAttribute("aria-controls")).closest(".nav2-drawer__sec");
        const searchRow = document.querySelector(".nav2-drawer__top");
        if (!scroller || !section) return;
        scroller.scrollTop +=
          section.getBoundingClientRect().top - scroller.getBoundingClientRect().top - (searchRow ? searchRow.offsetHeight : 0);
      }, 300);
    });
  }

  // Mobile category strip: sticks under the pinned header, slides away on scroll down, back on scroll up
  function initStripTuck() {
    const strip = document.querySelector(".nav2-strip");
    const section = document.querySelector(".section-header");
    const wrapper = section && section.querySelector(".header-wrapper");
    if (!strip || !wrapper || wrapper.dataset.stickyType !== "always") return;

    const mobile = window.matchMedia("(max-width: 1024px)");
    const TUCK_AFTER = 200;
    const THRESHOLD = 8;
    let lastY = window.scrollY;

    new ResizeObserver(() => strip.style.setProperty("--nav2-strip-top", `${section.offsetHeight}px`)).observe(section);
    strip.classList.add("nav2-strip--sticky");
    // Never leave keyboard focus on a pill that has slid behind the header
    strip.addEventListener("focusin", () => strip.classList.remove("is-tucked"));

    window.addEventListener(
      "scroll",
      () => {
        const y = window.scrollY;
        if (!mobile.matches) return;
        if (y < TUCK_AFTER) {
          strip.classList.remove("is-tucked");
        } else if (y - lastY > THRESHOLD) {
          strip.classList.add("is-tucked");
        } else if (lastY - y > THRESHOLD) {
          strip.classList.remove("is-tucked");
        } else {
          return;
        }
        lastY = y;
      },
      { passive: true }
    );
  }

  // Badges on bar items take width the bar may not have: if the last item would pass the bar's edge,
  // hide bar badges from the right until it fits. Panel and mobile menu badges are unaffected
  function initBarBadges(nav) {
    const bar = nav.querySelector(".nav2__bar");
    const badges = bar ? [...bar.querySelectorAll(":scope > .nav2__item > .nav2__link .nav2__pop")] : [];
    if (!badges.length) return;
    const overflows = () => {
      const items = [...bar.children].filter((item) => item.offsetWidth);
      return items.length > 0 && items[items.length - 1].getBoundingClientRect().right > nav.getBoundingClientRect().right + 0.5;
    };
    const fit = () => {
      badges.forEach((badge) => (badge.hidden = false));
      for (let i = badges.length - 1; i >= 0 && overflows(); i--) badges[i].hidden = true;
    };
    fit();
    window.addEventListener("resize", fit);
    if (document.fonts) document.fonts.ready.then(fit);
  }

  // Theme editor: show the dropdown holding a promo tile when its block is selected, and re-attach the
  // navigation after the editor redraws the header
  function initEditor() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    document.addEventListener("shopify:block:select", (event) => {
      const tile = [...document.querySelectorAll("[data-nav2] [data-shopify-editor-block]")].find((el) => {
        try {
          return JSON.parse(el.dataset.shopifyEditorBlock).id === event.detail.blockId;
        } catch (e) {
          return false;
        }
      });
      const item = tile && tile.closest(".nav2__item--has-panel");
      document.querySelectorAll("[data-nav2] .nav2__item.is-open").forEach((open) => open.classList.remove("is-open"));
      if (item) item.classList.add("is-open");
    });
    document.addEventListener("shopify:block:deselect", () => {
      document.querySelectorAll("[data-nav2] .nav2__item.is-open").forEach((open) => open.classList.remove("is-open"));
    });
    // Page-wide click and key handlers already cover redrawn elements; only element-bound parts are re-attached
    document.addEventListener("shopify:section:load", (event) => {
      const nav = event.target.querySelector("[data-nav2]");
      if (nav) {
        initDesktop(nav);
        initBarBadges(nav);
        initCampaigns();
      }
      if (event.target.querySelector(".nav2-strip")) initStripTuck();
    });
  }

  function init() {
    const nav = document.querySelector("[data-nav2]");
    const closeDesktop = nav ? initDesktop(nav) : null;
    if (nav) initBarBadges(nav);
    initSearchLinks(closeDesktop);
    initStrip();
    initStripTuck();
    initDrawerMore();
    initDrawerKeys();
    initCampaigns();
    window.addEventListener("pageshow", updateCampaignSchedules);
    window.addEventListener("pagehide", () => clearTimeout(campaignScheduleTimer));
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) updateCampaignSchedules();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      init();
      initEditor();
    });
  } else {
    init();
    initEditor();
  }
})();
